import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { cn } from '../lib/utils';
import { ThemeToggle } from './ThemeToggle';
import { ProfileMenu } from './ProfileMenu';
import { BalanceCard } from './BalanceCard';
import { BrandLogo } from './BrandLogo';
import { StatusIndicator } from './StatusIndicator';
import {
  LayoutDashboard,
  KeyRound,
  BarChart3,
  CreditCard,
  BookOpen,
  Settings,
  ChevronRight,
  Menu,
  X,
} from 'lucide-react';

/**
 * AppShell — the main dashboard frame.
 * Sidebar on the left (collapsible drawer on mobile), topbar with breadcrumb + user menu,
 * main content area.
 */
export function AppShell({
  user,                // { email, plan_code } | null
  billing,             // { credit_balance, plan_code, per_credit_cents, ... } | null
  navigation,         // [{ path, label, icon, badge? }]
  pathname = window.location.pathname,
  onSignOut,
  children,
  brandLabel = 'Datames',
  brandSubtitle = 'Customer',
}) {
  // Mobile drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Close drawer on route change
  useEffect(() => {
    setDrawerOpen(false);
  }, [pathname]);

  // Close drawer on Escape
  useEffect(() => {
    if (!drawerOpen) return undefined;
    function onKey(e) {
      if (e.key === 'Escape') setDrawerOpen(false);
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [drawerOpen]);

  // Lock body scroll when drawer is open
  useEffect(() => {
    if (drawerOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev;
      };
    }
    return undefined;
  }, [drawerOpen]);

  return (
    <div className="flex h-screen bg-bg text-fg overflow-hidden">
      {/* Desktop sidebar — always visible at md+. Hidden on mobile. */}
      <Sidebar
        user={user}
        billing={billing}
        navigation={navigation}
        pathname={pathname}
        brandLabel={brandLabel}
        brandSubtitle={brandSubtitle}
        onSignOut={onSignOut}
        className="hidden md:flex"
      />

      {/* Mobile drawer — slides in from the left, same content as the desktop sidebar. */}
      <MobileDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        user={user}
        billing={billing}
        navigation={navigation}
        pathname={pathname}
        brandLabel={brandLabel}
        brandSubtitle={brandSubtitle}
        onSignOut={onSignOut}
      />

      <div className="flex-1 flex flex-col min-w-0">
        <Topbar
          user={user}
          onSignOut={onSignOut}
          pathname={pathname}
          navigation={navigation}
          onOpenDrawer={() => setDrawerOpen(true)}
        />
        <main className="flex-1 overflow-auto">
          <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}

function Sidebar({ user, billing, navigation, pathname, brandLabel, brandSubtitle, onSignOut, className }) {
  return (
    <aside className={cn('w-60 flex-col bg-surface border-r border-border', className)}>
      {/* Brand */}
      <div className="px-5 py-5 border-b border-border">
        <BrandLogo size="sm" link="/" brandLabel={brandLabel} brandSubtitle={brandSubtitle} />
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
        {navigation.map((item) => (
          <NavItem key={item.path} {...item} active={pathname === item.path} />
        ))}
      </nav>

      {/* Balance card (above profile) */}
      <BalanceCard billing={billing} />

      {/* User profile menu (bottom-left, collapsible). Sign-out lives here. */}
      <ProfileMenu user={user} onSignOut={onSignOut} />
    </aside>
  );
}

/**
 * Mobile drawer — only rendered when open. Slides in from the left.
 */
function MobileDrawer({
  open, onClose, user, billing, navigation, pathname, brandLabel, brandSubtitle, onSignOut,
}) {
  if (!open) return null;
  return (
    <div className="md:hidden fixed inset-0 z-40">
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Close menu"
        onClick={onClose}
        className="absolute inset-0 bg-black/40 backdrop-blur-sm animate-fade-in"
      />
      {/* Drawer panel */}
      <div className="absolute inset-y-0 left-0 w-72 max-w-[85vw] bg-surface shadow-2xl flex flex-col animate-slide-in-left">
        {/* Brand + close */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <BrandLogo
            size="sm"
            link="/"
            brandLabel={brandLabel}
            brandSubtitle={brandSubtitle}
            onClick={onClose}
          />
          <button
            type="button"
            onClick={onClose}
            aria-label="Close menu"
            className="inline-flex items-center justify-center h-8 w-8 rounded-md text-fg-muted hover:text-fg hover:bg-surface-2 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
          {navigation.map((item) => (
            <NavItem key={item.path} {...item} active={pathname === item.path} />
          ))}
        </nav>

        {/* Balance card */}
        <BalanceCard billing={billing} />

        {/* Profile menu — closes drawer when item is clicked (ProfileMenu itself doesn't navigate-and-close) */}
        <ProfileMenu user={user} onSignOut={() => { onClose(); onSignOut?.(); }} />
      </div>
    </div>
  );
}

function NavItem({ path, label, icon: Icon, badge, active }) {
  return (
    <NavLink
      to={path}
      className={cn(
        'group flex items-center gap-2.5 px-2.5 py-2 rounded-md text-sm font-medium transition-colors',
        active
          ? 'bg-surface-2 text-fg'
          : 'text-fg-muted hover:text-fg hover:bg-surface-2/60'
      )}
    >
      <Icon className={cn('h-4 w-4', active ? 'text-brand' : 'text-fg-subtle')} />
      <span className="flex-1">{label}</span>
      {badge != null && (
        <span
          className={cn(
            'inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-md text-2xs font-semibold',
            active ? 'bg-brand/20 text-brand' : 'bg-surface-3 text-fg-muted'
          )}
        >
          {badge}
        </span>
      )}
    </NavLink>
  );
}

function Topbar({ user, onSignOut, pathname, navigation, onOpenDrawer }) {
  // Derive breadcrumb from pathname
  const current = navigation.find((n) => n.path === pathname) || navigation[0];
  return (
    <header className="flex items-center justify-between gap-4 px-4 sm:px-6 lg:px-8 h-14 border-b border-border bg-bg/80 backdrop-blur-md sticky top-0 z-10">
      <div className="flex items-center gap-2 text-sm min-w-0">
        {/* Mobile menu button — sits next to the theme toggle */}
        <button
          type="button"
          onClick={onOpenDrawer}
          aria-label="Open menu"
          className="md:hidden inline-flex items-center justify-center h-8 w-8 rounded-md text-fg-muted hover:text-fg hover:bg-surface-2 transition-colors shrink-0"
        >
          <Menu className="h-4 w-4" />
        </button>
        <span className="text-fg-subtle hidden sm:inline">Dashboard</span>
        <ChevronRight className="h-3.5 w-3.5 text-fg-subtle hidden sm:inline" />
        <span className="text-fg font-medium truncate">{current?.label || ''}</span>
      </div>
      {/* Right side: status indicator + theme toggle.
          Sign-out was moved to the profile menu in the sidebar (bottom-left). */}
      <div className="flex items-center gap-1">
        <StatusIndicator />
        <ThemeToggle />
      </div>
    </header>
  );
}

/**
 * Legacy export kept for backwards compatibility. The bottom tab bar was
 * removed in Phase-9 — mobile uses a slide-out drawer instead. This is a
 * no-op stub so any old import doesn't crash.
 */
export function MobileNav({ navigation, pathname }) {
  return null;
}